import { useSyncExternalStore } from 'react'

/**
 * Shared state for "an error page (404/500) is currently shown". The root
 * layout reads it to hide global chrome (the footer) and to apply the error
 * page's document title — the root is the single writer of document.title, so
 * the error page reports its title here rather than setting it directly (a
 * child effect would otherwise be clobbered by the root's meta effect, which
 * runs after it).
 *
 * Uses an external store rather than a CustomEvent because React runs child
 * effects before parent effects: on a direct load the ErrorPage would signal
 * before the root attached its listener. useSyncExternalStore re-reads the
 * snapshot on subscribe, so it picks up a value set before subscription.
 */
export interface ErrorPageState {
  active: boolean
  title?: string
}

// Single stable reference while unchanged — required by useSyncExternalStore.
let state: ErrorPageState = { active: false }
const listeners = new Set<() => void>()

export function setErrorPage(next: ErrorPageState) {
  if (state.active === next.active && state.title === next.title) return
  state = next
  for (const listener of listeners) listener()
}

function subscribe(callback: () => void) {
  listeners.add(callback)
  return () => listeners.delete(callback)
}

function getSnapshot() {
  return state
}

export function useErrorPage() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
